import { httpClient } from "./httpClient";

import { FluxoAprovacaoFiltrosCriteria, FluxoAprovacaoFiltrosOptions } from "@workflow/types";

import {
  DecidirAprovacaoPayload,
  FluxoAprovacaoDTO,
  FluxoAprovacaoEventoDTO,
  FluxoAprovacaoResumoDTO,
  HistoricoFluxosResumo,
  MinhasAprovacoesContagemItem,
} from "@/types/fluxo-aprovacao.type";
import { PageResponse } from "@/types/http.type";
import { buildQueryParams } from "@/utils/build-query-params";

/**
 * Cliente HTTP do **runtime do motor de aprovação genérico** (`/fluxo-aprovacao`,
 * recurso irmão da config `/configuracao-aprovacao`). Reutilizável por qualquer
 * processo — não conhece custo/despesa/comissão. A "minha fila" NÃO mora aqui: cada
 * processo tem endpoint dedicado que fixa o subtipo no backend (ex.: custo-aprovacao.service).
 */
class FluxoAprovacaoService {
  private readonly basePath = "/api/v1/fluxo-aprovacao";

  /** Detalhe de um fluxo (etapas + decisões). */
  public async detalhe(id: number): Promise<FluxoAprovacaoDTO> {
    return httpClient.get<FluxoAprovacaoDTO>(`${this.basePath}/${id}`);
  }

  /** Histórico global (Admin) — listagem paginada/filtrada server-side. */
  public async historicoListagem(
    filter?: FluxoAprovacaoFiltrosCriteria,
  ): Promise<PageResponse<FluxoAprovacaoResumoDTO>> {
    let url = `${this.basePath}/historico/listagem`;
    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }
    return httpClient.get<PageResponse<FluxoAprovacaoResumoDTO>>(url);
  }

  /** Opções de filtro do histórico global (Admin). */
  public async historicoFiltros(): Promise<FluxoAprovacaoFiltrosOptions> {
    return httpClient.get<FluxoAprovacaoFiltrosOptions>(`${this.basePath}/historico/filtros`);
  }

  /** KPIs do histórico (Admin) — contagem por situação. */
  public async historicoResumo(): Promise<HistoricoFluxosResumo> {
    return httpClient.get<HistoricoFluxosResumo>(`${this.basePath}/historico/resumo`);
  }

  /** Timeline de eventos (trilha append-only) de um fluxo, em ordem cronológica. */
  public async eventos(id: number): Promise<FluxoAprovacaoEventoDTO[]> {
    return httpClient.get<FluxoAprovacaoEventoDTO[]>(`${this.basePath}/${id}/eventos`);
  }

  /**
   * Contagem das minhas aprovações pendentes, agrupada por processo/subtipo — alimenta os
   * badges do menu numa única ida (COUNT no servidor, sem carregar a fila).
   */
  public async minhasContagem(): Promise<MinhasAprovacoesContagemItem[]> {
    return httpClient.get<MinhasAprovacoesContagemItem[]>(`${this.basePath}/minhas/contagem`);
  }

  /**
   * Registra a decisão do ator na etapa corrente (aprovar/reprovar). `silenciarErro`
   * suprime o toast central de erro — usado na aprovação em lote, onde um resumo único
   * substitui os avisos individuais.
   */
  public async decidir(
    id: number,
    payload: DecidirAprovacaoPayload,
    opcoes?: { silenciarErro?: boolean },
  ): Promise<FluxoAprovacaoDTO> {
    return httpClient.post<FluxoAprovacaoDTO>(`${this.basePath}/${id}/decidir`, payload, {
      toastConfig: opcoes?.silenciarErro ? { showError: false } : undefined,
    });
  }
}

export const fluxoAprovacaoService = new FluxoAprovacaoService();
